/**
 * Creating a custom button here for "toggle" button behavior. Styling is different than our existing buttons
 * so I've used a div with button implementation so I don't have to reset styles or have regression issues.
 * Button implementation requirements can be found here: https://www.w3.org/WAI/ARIA/apg/example-index/button/button.html
 */

import type { JSX, PropsWithChildren } from 'react';

import { cn } from '@change/ds-utils';

const baseStyles = 'items-center rounded-lg elevation-low flex justify-center p-10';
const hoverStyles = 'hover:bg-fill-hovered-weak hover:cursor-pointer';
const selectedStyles =
	'aria-pressed:elevation-elevated-selected aria-pressed:border-none aria-pressed:relative aria-pressed:before:content-[""] aria-pressed:before:absolute aria-pressed:before:inset-0 aria-pressed:before:rounded-[inherit] aria-pressed:before:p-0.5 aria-pressed:before:bg-linear-to-t aria-pressed:before:from-[#FE9311] aria-pressed:before:to-[#FFDB00] aria-pressed:before:[mask:linear-gradient(#000_0_0)_content-box_exclude,linear-gradient(#000_0_0)] aria-pressed:before:mask-exclude';
const errorStyles = 'group-aria-invalid:border-2 group-aria-invalid:border-stroke-error-strong';

type ChangeEvent = React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>;

export type ToggleButtonProps<VALUE> = {
	onChange?: (e: ChangeEvent, value: VALUE) => void;
	value: VALUE;
	selected?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'role'>;

export function ToggleButton<VALUE>({
	children,
	onChange,
	selected,
	value,
	className,
	...rest
}: PropsWithChildren<ToggleButtonProps<VALUE>>): JSX.Element {
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) return;

		switch (e.key) {
			case 'Enter': {
				e.preventDefault();
				onChange(e, value);
				break;
			}
			// The default space key behavior is to scroll the page, so we should
			// prevent the default behavior and activate the action handler on keyup instead
			// to mimic native button events
			case ' ':
			case 'Spacebar': {
				e.preventDefault();
				break;
			}
			default:
				break;
		}
	};

	const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) return;

		switch (e.key) {
			case ' ':
			case 'Spacebar': {
				e.preventDefault();
				onChange(e, value);
				break;
			}
			default:
				break;
		}
	};

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!onChange) return;

		onChange(e, value);
	};

	return (
		// eslint-disable-next-line @change-corgi/require-data-qa-attribute -- consumer handles data-qa attributes
		<div
			role="button"
			aria-pressed={selected}
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			className={cn([className, baseStyles, hoverStyles, selectedStyles, errorStyles])}
			{...rest}
		>
			{children}
		</div>
	);
}
